import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, Check, Star, ShieldCheck } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import Seo from "@/components/Seo";
import { Reveal, MaskedLines } from "@/components/Reveal";
import AppointmentForm from "@/components/AppointmentForm";
import { SITE, waLink } from "@/lib/site";
import {
  CONDITIONS,
  TREATMENTS,
  WHY_US,
  JOURNEY,
  HOME_FAQS,
} from "@/data/content";

const IMG = {
  doctor: "/dr-sanjay-teza.jpg",
  clinic: "https://customer-assets-agu9un31.emergentagent.net/job_page-from-doc-6/artifacts/2kdgcw2y_IMG_3200.webp",
  consult: "https://customer-assets-agu9un31.emergentagent.net/job_page-from-doc-6/artifacts/v2qk945z_IMG_3108.webp",
};

const homeSchemas = [
  {
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    "@id": "https://entspecialistvaranasi.com/#home",
    url: "https://entspecialistvaranasi.com/",
    name: "Dr. Sanjay Teza — ENT Specialist in Varanasi",
    inLanguage: "en-IN",
    isPartOf: { "@id": "https://entspecialistvaranasi.com/#website" },
    about: { "@id": "https://entspecialistvaranasi.com/#doctor" },
    primaryImageOfPage: "https://entspecialistvaranasi.com/dr-sanjay-teza.jpg",
  },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const badgeY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <>
      <Seo
        title="Dr. Sanjay Teza — ENT Specialist in Varanasi | Ear, Nose & Throat Care"
        description="Dr. Sanjay Teza is a leading ENT specialist in Varanasi treating ear, nose, throat, vertigo and children's ENT conditions plus advanced surgery. Book your consultation today."
        path="/"
        schemas={homeSchemas}
      />

      {/* HERO */}
      <section ref={heroRef} className="relative pt-32 md:pt-40 pb-16 overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1, duration: 0.6 }}
              className="text-xs md:text-sm tracking-[0.24em] uppercase text-brand-pine mb-8 flex items-center gap-3"
            >
              <span className="inline-block w-10 h-px bg-brand-pine" />
              ENT Specialist · Varanasi
            </motion.p>

            <h1 className="font-heading font-bold text-brand-ink text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
              <MaskedLines
                lines={["Breathe, hear and", "live clearly again"]}
                lineClassName=""
              />
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.8 }}
              className="mt-8 text-lg leading-relaxed text-brand-muted max-w-xl"
            >
              Expert, unhurried ear, nose & throat care from {SITE.doctorName} — combining{" "}
              {SITE.experienceYears}+ years of experience with modern, minimally invasive
              treatments for the whole family.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.05, duration: 0.8 }}
              className="mt-10 flex flex-col sm:flex-row gap-4"
            >
              <a
                href={waLink(`Hello, I would like to book an appointment with ${SITE.doctorName}.`)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-book-btn"
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-brand-teal px-8 py-4 text-sm text-brand-ink transition-[background-color,transform] duration-200 hover:bg-brand-tealHover hover:scale-[1.02]"
              >
                Book an appointment
                <ArrowUpRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
              <Link
                to="/ent-treatment-guide-varanasi"
                data-testid="hero-explore-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-brand-ink/20 px-8 py-4 text-sm text-brand-ink transition-colors duration-200 hover:border-brand-pine hover:text-brand-pine"
              >
                Explore treatments
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2, duration: 0.8 }}
              className="mt-12 flex items-center gap-8"
            >
              <div>
                <p className="font-heading text-3xl text-brand-ink">{SITE.patientsTreated}</p>
                <p className="text-xs tracking-wide text-brand-muted">Patients treated</p>
              </div>
              <div className="w-px h-10 bg-brand-line" />
              <div className="flex items-center gap-1 text-brand-pine">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-current" />
                ))}
                <span className="ml-2 text-xs tracking-wide text-brand-muted">Trusted care</span>
              </div>
            </motion.div>
          </div>

          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, scale: 1.05 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
              className="relative mx-auto w-full max-w-sm"
            >
              <div className="arch-top overflow-hidden rounded-b-2xl aspect-[4/5] bg-brand-mint/40">
                <motion.img
                  style={{ y: imgY }}
                  src={IMG.doctor}
                  alt={`${SITE.doctorName}, ENT Specialist in Varanasi`}
                  className="w-full h-[115%] object-cover -mt-4"
                  loading="eager"
                />
              </div>
              <motion.div
                style={{ y: badgeY }}
                className="absolute -left-6 bottom-10 bg-brand-surface border border-brand-line rounded-2xl p-5 shadow-[0_20px_40px_rgba(21,30,28,0.1)] w-52"
              >
                <div className="flex items-center gap-2 text-brand-pine mb-1">
                  <ShieldCheck className="w-5 h-5" />
                  <span className="font-heading text-2xl text-brand-ink">{SITE.experienceYears}+ yrs</span>
                </div>
                <p className="text-xs text-brand-muted leading-relaxed">
                  Experience across ear, nose, throat & advanced surgery
                </p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="py-8 border-y border-brand-line bg-brand-surface/60">
        <Marquee speed={40} gradient={false} autoFill>
          {["Ear Pain", "Vertigo", "Sinusitis", "Hearing Loss", "Tonsillitis", "Nasal Allergies", "Tinnitus", "Children's ENT"].map(
            (w, i) => (
              <span key={i} className="mx-10 font-heading text-2xl md:text-3xl text-brand-muted flex items-center gap-10">
                {w}
                <span className="text-brand-pine/40">✦</span>
              </span>
            )
          )}
        </Marquee>
      </section>

      {/* ABOUT */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-16 items-center">
          <Reveal className="lg:col-span-6 order-2 lg:order-1">
            <div className="overflow-hidden rounded-2xl aspect-[5/4]">
              <img src={IMG.clinic} alt="Modern ENT clinic interior in Varanasi" className="w-full h-full object-cover" loading="lazy" />
            </div>
          </Reveal>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <Reveal>
              <p className="text-xs tracking-[0.2em] uppercase text-brand-pine mb-5">About the Doctor</p>
              <h2 className="font-heading font-bold text-brand-ink text-3xl md:text-4xl lg:text-5xl leading-[1.1] tracking-tight">
                Careful diagnosis. Honest advice. Real results.
              </h2>
            </Reveal>
            <Reveal delay={1}>
              <p className="mt-6 text-lg leading-relaxed text-brand-muted">
                {SITE.doctorName} is a senior ENT specialist and surgeon in Varanasi with over
                25 years of experience and more than 3 lakh patients treated. Trained at Banaras
                Hindu University (BHU) with senior residency at Batra Hospital, New Delhi, he combines
                deep expertise with modern technology — modular operation theatres, Carl Zeiss
                operating microscopes and advanced Stryker endoscopy — for precise, minimally
                invasive care.
              </p>
            </Reveal>
            <Reveal delay={2}>
              <ul className="mt-8 space-y-3">
                {[
                  "MBBS & MS (ENT) — Banaras Hindu University (BHU)",
                  "Senior Residency in Head & Neck surgery — Batra Hospital, New Delhi",
                  "Health Legends & Dainik Jagran Wellness+ Healthcare Awards",
                  "Member — Indian Medical Association (IMA) & Association of Otolaryngologists of India (AOI)",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-brand-ink">
                    <span className="mt-0.5 grid place-items-center w-5 h-5 rounded-full bg-brand-mint text-brand-pine shrink-0">
                      <Check className="w-3 h-3" />
                    </span>
                    <span className="text-[15px]">{item}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="py-20 bg-brand-ink text-brand-bg">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <Reveal>
            <p className="text-xs tracking-[0.2em] uppercase text-brand-mint mb-4">Why Patients Choose Dr. Teza</p>
            <h2 className="font-heading font-bold text-4xl md:text-5xl max-w-2xl leading-tight">
              Trusted for expertise, experience & genuine care
            </h2>
          </Reveal>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-brand-bg/10 rounded-2xl overflow-hidden">
            {WHY_US.map((w, i) => (
              <Reveal key={w.label} delay={i} className="bg-brand-ink p-8">
                <p className="font-heading text-5xl text-brand-mint">{w.stat}</p>
                <p className="mt-3 text-sm font-semibold text-brand-bg">{w.label}</p>
                <p className="mt-2 text-sm text-brand-bg/55 leading-relaxed">{w.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CONDITIONS BENTO */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <Reveal className="max-w-2xl mb-14">
            <p className="text-xs tracking-[0.2em] uppercase text-brand-pine mb-4">Conditions Treated</p>
            <h2 className="font-heading font-bold text-brand-ink text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight">
              Complete ear, nose & throat care under one roof
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-5 auto-rows-fr">
            {CONDITIONS.map((c, i) => {
              const Icon = c.icon;
              return (
                <Reveal
                  key={c.title}
                  delay={i % 4}
                  className={`${c.span} group bg-brand-surface border border-brand-line rounded-2xl p-8 transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(21,30,28,0.07)] hover:border-brand-pine/30`}
                >
                  <div className="flex items-start justify-between">
                    <span className="grid place-items-center w-12 h-12 rounded-full bg-brand-mint/50 text-brand-pine">
                      <Icon className="w-6 h-6" />
                    </span>
                    <span className="text-[10px] tracking-[0.18em] uppercase text-brand-muted">{c.group}</span>
                  </div>
                  <h3 className="mt-6 font-heading text-2xl text-brand-ink">{c.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-brand-muted">{c.desc}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* PATIENT JOURNEY */}
      <section className="py-24 md:py-32 bg-brand-surface/60 border-y border-brand-line">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <Reveal className="max-w-2xl mb-16">
            <p className="text-xs tracking-[0.2em] uppercase text-brand-pine mb-4">The Patient Journey</p>
            <h2 className="font-heading font-bold text-brand-ink text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight">
              A calm, clear path from first visit to full recovery
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-4 gap-8">
            {JOURNEY.map((step, i) => (
              <Reveal key={step.n} delay={i} className="relative">
                <span className="font-heading text-6xl text-brand-teal">{step.n}</span>
                <div className="mt-2 h-px w-full bg-brand-line" />
                <h3 className="mt-5 font-heading text-2xl text-brand-ink">{step.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-brand-muted">{step.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ADVANCED TREATMENTS */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <Reveal className="max-w-2xl">
              <p className="text-xs tracking-[0.2em] uppercase text-brand-pine mb-4">Advanced Treatments</p>
              <h2 className="font-heading font-bold text-brand-ink text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight">
                Modern surgery, minimal downtime
              </h2>
            </Reveal>
            <Reveal delay={1}>
              <Link
                to="/ent-treatment-guide-varanasi"
                data-testid="treatments-viewall-link"
                className="inline-flex items-center gap-2 text-sm text-brand-pine hover:gap-3 transition-[gap] duration-200"
              >
                View full treatment guide <ArrowUpRight className="w-4 h-4" />
              </Link>
            </Reveal>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {TREATMENTS.map((t, i) => (
              <Reveal
                key={t.title}
                delay={i % 3}
                className="group bg-brand-surface border border-brand-line rounded-2xl p-8 flex flex-col transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(21,30,28,0.07)]"
              >
                <span className="text-[10px] tracking-[0.18em] uppercase text-brand-pine">{t.tag}</span>
                <h3 className="mt-4 font-heading text-2xl text-brand-ink">{t.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-brand-muted flex-1">{t.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ PREVIEW */}
      <section className="py-24 md:py-32 bg-brand-surface/60 border-y border-brand-line">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-4">
            <Reveal>
              <p className="text-xs tracking-[0.2em] uppercase text-brand-pine mb-4">FAQs</p>
              <h2 className="font-heading font-bold text-brand-ink text-3xl md:text-4xl leading-tight tracking-tight">
                Answers to common ENT questions
              </h2>
              <Link
                to="/ent-faqs-varanasi"
                data-testid="faq-viewall-link"
                className="mt-6 inline-flex items-center gap-2 text-sm text-brand-pine hover:gap-3 transition-[gap] duration-200"
              >
                See all 25 FAQs <ArrowUpRight className="w-4 h-4" />
              </Link>
            </Reveal>
          </div>
          <div className="lg:col-span-8">
            <Reveal>
              <Accordion type="single" collapsible className="w-full" data-testid="home-faq-accordion">
                {HOME_FAQS.map((f, i) => (
                  <AccordionItem key={i} value={`item-${i}`} className="border-brand-line">
                    <AccordionTrigger
                      data-testid={`home-faq-trigger-${i}`}
                      className="text-left font-heading text-xl md:text-2xl text-brand-ink py-6 hover:no-underline"
                    >
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-[15px] leading-relaxed text-brand-muted pb-6">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CTA — Appointment + WhatsApp */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-12 gap-0 rounded-3xl overflow-hidden border border-brand-line">
            <div className="lg:col-span-5 bg-brand-ink text-brand-bg p-10 md:p-14 flex flex-col justify-between">
              <div>
                <p className="text-xs tracking-[0.2em] uppercase text-brand-mint mb-4">Appointments</p>
                <h2 className="font-heading font-bold text-4xl md:text-5xl leading-tight">
                  Ready to feel better?
                </h2>
                <p className="mt-5 text-brand-bg/60 leading-relaxed max-w-sm">
                  Book a consultation with {SITE.doctorName}. Fill the form and we&apos;ll confirm on
                  WhatsApp, or message us directly right now.
                </p>
              </div>
              <div className="mt-10 space-y-4">
                <a
                  href={waLink(`Hello, I would like to book an appointment with ${SITE.doctorName}.`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="cta-whatsapp-btn"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-whatsapp px-7 py-3.5 text-sm text-white transition-colors hover:bg-whatsapp-hover w-full sm:w-auto"
                >
                  Chat on WhatsApp
                </a>
                <p className="text-sm text-brand-bg/50">
                  Or call{" "}
                  <a href={`tel:${SITE.phoneRaw}`} className="text-brand-mint underline underline-offset-4">
                    {SITE.phoneDisplay}
                  </a>
                </p>
              </div>
            </div>
            <div className="lg:col-span-7 bg-brand-surface p-10 md:p-14">
              <h3 className="font-heading text-2xl text-brand-ink mb-8">Request an appointment</h3>
              <AppointmentForm />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
